import { FC } from "react";
import "./JointAccountOwners.scss";
import {
  CoreFieldValue,
  LoadingV2,
  PrimaryButton,
  PrimaryCard,
  SectionPlaceholder,
} from "@ucl/library";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { JointAccountOwnerItem } from "./JointAccountOwnerItem";
import { JointAccountOwnerTableRow } from "./JointAccountOwnerTableRow";
import { useJointAccountOwners } from "@/hooks/useJointAccountOwners";
import {
  FinancialApplicationFormValidationDisplayBehavior,
  FinancialApplicationFormSectionContextType,
} from "@/hooks/useFinancialApplicationFormSectionValidation";
import { MessageComponent } from "@syncfusion/ej2-react-notifications";

export interface JointAccountOwnersProps {
  financialApplicationId: string;
  initiallyCollapseJointAccountOwnerForms?: boolean;
  readOnly?: boolean;
  renderCollapsedAsTable?: boolean;
  onBlur?: (field: CoreFieldValue) => void;
  validationBehavior?: FinancialApplicationFormValidationDisplayBehavior;
  validationContextType: FinancialApplicationFormSectionContextType;
  shouldHideSecurityQuestions?: boolean;
  shouldAskIfCanProvideGovernmentIdInformation?: boolean;
  shouldAlwaysShowGovernmentIdInformationFields?: boolean;
  shouldShowDigitalBankingFields?: boolean;
  shouldShowDebitCardFields?: boolean;
}

export const JointAccountOwners: FC<JointAccountOwnersProps> = (props) => {
  const {
    jointAccountOwnerIds,
    handleDeleteJointAccount,
    handleAddJointAccount,
    isLoading,
    isCreating,
    validationErrors,
  } = useJointAccountOwners(
    props.financialApplicationId,
    props.validationBehavior,
    props.validationContextType
  );

  return (
    <div className="joint-account-owner-info-accounts">
      {!props.readOnly && (
        <div className="joint-account-owner-info-accounts__header-info-validations">
          {Object.entries(validationErrors.fieldSchemaCollectionLevelValidationErrors).map(
            ([key, value]) => (
              <MessageComponent title={key} severity="Error">
                {value.join(", ")}
              </MessageComponent>
            )
          )}
        </div>
      )}
      <div className="joint-account-owner-info-accounts__header">
        {!props.readOnly && (
          <PrimaryButton
            text="Add Joint Owner"
            iconProps={{ icon: faPlus }}
            onClick={handleAddJointAccount}
            disabled={props.readOnly}
            isLoading={isCreating}
            loadingProgressContent="Add Joint Owner"
            loadingSuccessContent="Add Joint Owner"
          />
        )}
      </div>
      <div className="joint-account-owner-info-accounts__content">
        <PrimaryCard>
          {isLoading && <LoadingV2 />}
          {!isLoading && !props.readOnly && jointAccountOwnerIds.length === 0 && (
            <SectionPlaceholder label="No joint account owners. Add one to get started." />
          )}
          {!isLoading && props.readOnly && jointAccountOwnerIds.length === 0 && (
            <div className="empty-state-message">
              <p>No joint account owners have been added.</p>
            </div>
          )}
          {!isLoading && props.renderCollapsedAsTable && jointAccountOwnerIds.length > 0 && (
            <div className="joint-account-owners-table">
              <table className="joint-account-owners-table__table">
                <thead className="joint-account-owners-table__header">
                  <tr>
                    <th>Name</th>
                    <th>Account</th>
                    <th>SSN</th>
                    <th>Relationship to Owner</th>
                    <th>Address</th>
                    <th>Phone Number</th>
                  </tr>
                </thead>
                <tbody className="joint-account-owners-table__body">
                  {jointAccountOwnerIds.map((id) => (
                    <JointAccountOwnerTableRow
                      key={id}
                      financialApplicationId={props.financialApplicationId}
                      jointOwnerId={id}
                      onDelete={handleDeleteJointAccount}
                      validationErrors={
                        validationErrors.fieldSchemaCollectionMemberValidationErrors[id] || {}
                      }
                      readOnly={props.readOnly}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isLoading &&
            !props.renderCollapsedAsTable &&
            jointAccountOwnerIds.map((id) => (
              <JointAccountOwnerItem
                key={id}
                financialApplicationId={props.financialApplicationId}
                jointOwnerId={id}
                onDelete={handleDeleteJointAccount}
                validationErrors={
                  validationErrors.fieldSchemaCollectionMemberValidationErrors[id] || {}
                }
                onBlur={props.onBlur}
                isInitiallyCollapsed={props.initiallyCollapseJointAccountOwnerForms}
                allowChangingWhetherCollapsed={!props.readOnly}
                readOnly={props.readOnly}
                validationContextType={props.validationContextType}
                shouldHideSecurityQuestions={props.shouldHideSecurityQuestions}
                shouldAskIfCanProvideGovernmentIdInformation={props.shouldAskIfCanProvideGovernmentIdInformation}
                shouldAlwaysShowGovernmentIdInformationFields={props.shouldAlwaysShowGovernmentIdInformationFields}
                shouldShowDigitalBankingFields={props.shouldShowDigitalBankingFields}
                shouldShowDebitCardFields={props.shouldShowDebitCardFields}
              />
            ))}
        </PrimaryCard>
      </div>
    </div>
  );
};
